// UPPDRAG-36 — shared clickable player name button.
//
// Generalized version of the inline PlayerNameButton in AdminTeams.tsx
// (UPPDRAG-23 / UPPDRAG-27). The AdminTeams version is tightly coupled
// to the team-member shape with embedded `profile.customers[0]`. This
// shared variant takes already-resolved `customerId` + `name` props so
// callers from any data shape can use it without conforming to the
// team-member structure.
//
// Behaviour:
//   - Click: navigate to /manager/customers/:customerId
//   - Click with null customerId: toast.info "Ingen kundprofil hittades"
//   - Optional `onAfterNavigate` callback for callers that need to
//     close the surrounding modal/sheet on navigate (e.g.
//     BookingDetailModal closes its <Sheet> so the new route is
//     visible without overlay).
//
// AdminTeams.tsx still has its own inline copy of PlayerNameButton —
// migrating it to this shared module is out of scope for UPPDRAG-36
// (would touch many call sites). A separate UPPDRAG can DRY them up.

import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface PlayerNameButtonProps {
  /** Customer row id. Null = no customer profile in this org. */
  customerId: string | null;
  /** Display name. Caller resolves the fallback chain. */
  name: string;
  /** Apply muted (secondary) styling. */
  muted?: boolean;
  /** Run after successful navigate — typically `() => onOpenChange(false)`. */
  onAfterNavigate?: () => void;
  /** Override the wrapper class. */
  className?: string;
}

export function PlayerNameButton({
  customerId,
  name,
  muted = false,
  onAfterNavigate,
  className,
}: PlayerNameButtonProps) {
  const navigate = useNavigate();
  const handleClick = () => {
    if (!customerId) {
      toast.info(`Ingen kundprofil hittades för ${name}`);
      return;
    }
    navigate(`/manager/customers/${customerId}`);
    onAfterNavigate?.();
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        "text-left transition-colors",
        muted
          ? "text-muted-foreground hover:text-foreground hover:underline"
          : "hover:underline hover:text-primary",
        className,
      )}
    >
      {name}
    </button>
  );
}
